import { useEffect, useState } from "react";

import type { PDFDocumentProxy } from "pdfjs-dist";

export type PdfStatus = "loading" | "ready" | "error";

async function loadPdf(url: string): Promise<PDFDocumentProxy> {
    const [pdfjs, { default: workerUrl }] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    return pdfjs.getDocument({ url }).promise;
}

// The `[[TERM: ...]]` placeholders a drafted agreement still carries (the
// hub's fill-agreement.py prints them in red until the terms are agreed).
// Each page's text is joined first, so a marker split across text runs still
// matches. Distinct labels, in document order.
export async function findOpenTerms(url: string): Promise<string[]> {
    const doc = await loadPdf(url);
    const found = new Set<string>();
    for (let n = 1; n <= doc.numPages; n++) {
        const content = await (await doc.getPage(n)).getTextContent();
        const text = content.items.map((i) => ("str" in i ? i.str : "")).join(" ").replace(/\s+/g, " ");
        for (const m of text.matchAll(/\[ ?\[ ?TERM ?: ?([^\]]*?) ?\] ?\]/g)) found.add(m[1] || "unnamed term");
    }
    void doc.cleanup();
    return [...found];
}

// Load a document with pdf.js. Shared by the paginated viewer (PdfPages) and
// the scrolling one (PdfScroll). The callers mount one viewer per document
// (keyed by url) and unmount it when done, so `loading` is the fresh-mount
// state and nothing here ever has to reset.
export function usePdfDocument(url: string): { doc: PDFDocumentProxy | null; status: PdfStatus } {
    const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
    const [status, setStatus] = useState<PdfStatus>("loading");
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const loaded = await loadPdf(url);
                if (cancelled) return;
                setDoc(loaded);
                setStatus("ready");
            } catch {
                if (!cancelled) setStatus("error");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [url]);
    return { doc, status };
}
